/**
 * Leuchtkasten für Screenshots: Klick auf ein Vorschaubild öffnet die
 * Vollbild-Ansicht (samt Annotationen, via ScreenshotBild gross).
 *
 * Bedienung: Klick auf den Hintergrund oder Escape schließt; bei mehreren
 * Bildern blättern Pfeiltasten und die Randknöpfe. Der Zähler unten zeigt
 * die Position. Gerendert wird nur, solange offen — kein verstecktes DOM.
 */

import { useEffect } from "react"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import { ScreenshotBild } from "@/components/erfassung/ScreenshotBild"
import type { Screenshot } from "@/types"

interface Props {
  reportId: number
  screenshots: Screenshot[]
  /** Index des angezeigten Bildes */
  index: number
  onIndexChange: (index: number) => void
  onClose: () => void
}

export function ScreenshotLeuchtkasten({
  reportId,
  screenshots,
  index,
  onIndexChange,
  onClose,
}: Props) {
  const mehrere = screenshots.length > 1
  const zurueck = () => onIndexChange((index - 1 + screenshots.length) % screenshots.length)
  const weiter = () => onIndexChange((index + 1) % screenshots.length)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      else if (mehrere && e.key === "ArrowLeft") zurueck()
      else if (mehrere && e.key === "ArrowRight") weiter()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  })

  const screenshot = screenshots[index]
  if (!screenshot) return null

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/85 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-label={`Screenshot ${index + 1} von ${screenshots.length}`}
    >
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={onClose}
          title="Schließen (Esc)"
          aria-label="Schließen"
          className="rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="relative min-h-0 flex-1" onClick={(e) => e.stopPropagation()}>
        <ScreenshotBild reportId={reportId} screenshot={screenshot} gross />
        {mehrere && (
          <>
            <button
              type="button"
              onClick={zurueck}
              title="Vorheriges Bild (←)"
              aria-label="Vorheriges Bild"
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2.5 text-white/90 hover:bg-black/70"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={weiter}
              title="Nächstes Bild (→)"
              aria-label="Nächstes Bild"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2.5 text-white/90 hover:bg-black/70"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
      </div>

      <p className="pt-3 text-center text-[13px] font-semibold text-white/70">
        {index + 1} / {screenshots.length}
      </p>
    </div>
  )
}
